import clsx from 'clsx'
import {
  AcademyIllustrationIcon,
  SchoolIllustrationIcon,
  TeacherIllustrationIcon,
} from '@weblite/icons'
import { useHistoryCustomBack } from 'weblite-router'
import { ReactNode, useMemo } from 'react'
import { Radio, styled, Typography } from '@mui/material'
import { t } from '$i18n'
import { closeAuthSelectOrganizerType } from '$logic/router/service'
import { OrganizerType } from '$logic/profile'

export function SelectOrganizerType({
  value,
  onChange,
}: {
  value?: OrganizerType
  onChange: (value: OrganizerType) => void
}) {
  const organizerTypes = useMemo(() => {
    return [
      {
        type: OrganizerType.TEACHER,
        title: t('signup.selectOrganizerType.options.person.title'),
        description: t('signup.selectOrganizerType.options.person.description'),
        icon: <TeacherIllustrationIcon />,
      },
      {
        type: OrganizerType.ACADEMY,
        title: t('signup.selectOrganizerType.options.academy.title'),
        description: t(
          'signup.selectOrganizerType.options.academy.description',
        ),
        icon: <SchoolIllustrationIcon />,
      },
      {
        type: OrganizerType.SCHOOL,
        title: t('signup.selectOrganizerType.options.school.title'),
        description: t('signup.selectOrganizerType.options.school.description'),
        icon: <AcademyIllustrationIcon />,
      },
    ]
  }, [])

  useHistoryCustomBack(() => void closeAuthSelectOrganizerType(), [])

  return (
    <OptionsContainer>
      {organizerTypes.map(organizer => (
        <SelectOption
          selected={organizer.type === value}
          title={organizer.title}
          icon={organizer.icon}
          description={organizer.description}
          key={organizer.type}
          value={organizer.type}
          onSelect={onChange}
        />
      ))}
    </OptionsContainer>
  )
}

const OptionsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
`

function SelectOption({
  icon,
  title,
  description,
  selected,
  onSelect,
  value,
}: {
  title: string
  description: string
  icon: ReactNode
  selected?: boolean
  onSelect: (value: OrganizerType) => void
  value: OrganizerType
}) {
  const handleSelect = (val: OrganizerType) => () => {
    onSelect(val)
  }
  return (
    <SelectOptionWrapper
      onClick={handleSelect(value)}
      className={clsx({ selected })}
    >
      {icon}
      <SelectOptionTitleDesc>
        <Typography variant="subtitle2" color="neutral900">
          {title}
        </Typography>
        <Typography variant="paragraph2" color="neutral700">
          {description}
        </Typography>
      </SelectOptionTitleDesc>
      <Radio checked={selected} />
      <SelectOptionFieldSet className={clsx({ selected })} />
    </SelectOptionWrapper>
  )
}

const SelectOptionWrapper = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  position: relative;
  transition: background-color 0.3s ease;
  &.selected {
    background-color: ${({ theme }) => theme.palette.primary50};
  }
  & > svg {
    width: 40px;
    height: auto;
    flex-shrink: 0;
  }
`

const SelectOptionFieldSet = styled('div')`
  border: 1px solid ${({ theme }) => theme.palette.neutral300};
  border-radius: 12px;
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  transition: border-color 0.3s ease;
  width: 100%;
  height: 100%;

  &.selected {
    border: 2px solid ${({ theme }) => theme.palette.primary500};
  }
`

const SelectOptionTitleDesc = styled('div')`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: 4px;
`
